import { isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import { safeQueryDecode } from '@/utils/condition-query-encoding';
import { parseConditions } from '@/features/condition-viewer';
import type { ConditionNode } from '@/features/condition-viewer/types';

/**
 * URL/param helpers for the People list page. The `filters` param carries a
 * Base64-encoded condition tree (either the legacy serialized array or an
 * `{ and: [...] }` / `{ or: [...] }` envelope).
 */

type DecodedFilters = ReturnType<typeof safeQueryDecode>;

/** Decode the `filters` search param; `[]` for absent/undecodable values. */
export function decodeFilters(filters: string | null): DecodedFilters {
  if (filters == null || isEmptyString(filters)) return [];
  return safeQueryDecode(filters);
}

function isEmptyFilterNode(node: unknown): boolean {
  if (node == null) return true;
  if (Array.isArray(node)) return node.length === 0 || node.every(isEmptyFilterNode);
  if (!isRecord(node)) return false;
  if ('and' in node) return isEmptyFilterNode(node.and);
  if ('or' in node) return isEmptyFilterNode(node.or);
  if ('not' in node) return isEmptyFilterNode(node.not);
  return Object.keys(node).length === 0;
}

/** True when the encoded filter query decodes to no effective conditions (`[]`, `{and:[]}`, …). */
export function isEmptyFiltersQuery(filters: string | null): boolean {
  if (filters == null || filters.length === 0) return true;
  return isEmptyFilterNode(decodeFilters(filters));
}

/**
 * No effective search is active: filters absent/empty, and the email term is
 * blank or the bare `@` wildcard. Gates the cross-page select-all link and
 * flips bulk delete back to explicit ids.
 */
export function noFiltersApplied({ filters, email }: { filters: string | null; email: string }): boolean {
  const trimmed = email.trim();
  return isEmptyFiltersQuery(filters) && (isEmptyString(trimmed) || trimmed === '@');
}

/**
 * The API reports matching results but returned none on the first page —
 * sensitive-attribute filtering hid them from this viewer. An unknown total
 * (`-1`) is not a report of matches, so it never counts as hidden.
 */
export function areResultsHidden({
  total,
  page,
  rowCount,
}: {
  total: number;
  page: number;
  rowCount: number;
}): boolean {
  return total > 0 && page <= 1 && rowCount === 0;
}

/**
 * Whether the current `page` param is valid for the loaded result set. An
 * unknown total (`-1`) only validates the param itself; otherwise the page
 * must be within the last page.
 */
export function isValidPaginationPage({ total, size, page }: { total: number; size: number; page: number }): boolean {
  if (!Number.isFinite(page) || page < 1) return false;
  if (total < 0) return true;
  return page <= Math.ceil(total / size);
}

export interface FilterConditionCounts {
  segments: number;
  attributes: number;
  newsletters: number;
  messages: number;
  events: number;
  relationship_attributes: number;
}

function countNode(node: ConditionNode, counts: FilterConditionCounts): void {
  switch (node.type) {
    case 'group':
      for (const child of node.children) countNode(child, counts);
      return;
    case 'segment':
      counts.segments += 1;
      return;
    case 'attribute':
      counts.attributes += 1;
      return;
    case 'inbound':
      counts.newsletters += 1;
      return;
    case 'message':
      counts.messages += 1;
      return;
    case 'event':
      counts.events += 1;
      return;
    case 'relationship':
      counts.relationship_attributes += 1;
      return;
    default:
      return;
  }
}

/**
 * Per-type condition counts plus the top-level operator, for the
 * list-viewed analytics event. Counting is over the parsed condition tree.
 */
export function summarizeFilterConditions(filters: string | null): FilterConditionCounts & { operator: string } {
  const counts: FilterConditionCounts = {
    segments: 0,
    attributes: 0,
    newsletters: 0,
    messages: 0,
    events: 0,
    relationship_attributes: 0,
  };
  const decoded = decodeFilters(filters);
  const rootRecord = Array.isArray(decoded) ? { and: decoded } : decoded;
  const operator = Array.isArray(decoded) ? 'and' : (Object.keys(decoded)[0] ?? 'and');
  countNode(parseConditions(rootRecord), counts);
  return { ...counts, operator };
}
